import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api from '../services/api'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token')
      if (token) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          const response = await api.get('/auth/profile')
          setUser(response.data.data.user)
        } catch (err) {
          console.error('Auth init error:', err)
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          delete api.defaults.headers.common['Authorization']
        }
      }
      setLoading(false)
    }

    initAuth()
  }, [])

  // Login function
  const login = useCallback(async (username, password) => {
    setError(null)
    try {
      const response = await api.post('/auth/login', { username, password })
      const { accessToken, refreshToken, user: userData } = response.data.data

      // Store tokens
      localStorage.setItem('token', accessToken)
      localStorage.setItem('refreshToken', refreshToken)

      // Set auth header
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

      setUser(userData)
      return { success: true }
    } catch (err) {
      const message = err.response?.data?.message || 'Login gagal'
      setError(message)
      throw new Error(message)
    }
  }, [])

  // Logout function
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      delete api.defaults.headers.common['Authorization']
      setUser(null)
    }
  }, [])

  // Change password function
  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      await api.post('/auth/change-password', { oldPassword, newPassword })
      return { success: true }
    } catch (err) {
      const message = err.response?.data?.message || 'Gagal mengubah password'
      return { success: false, error: message }
    }
  }, [])

  // Check permission
  const hasPermission = useCallback((permission) => {
    if (!user) return false
    if (user.permissions?.includes('all')) return true
    if (Array.isArray(permission)) {
      return permission.some(p => user.permissions?.includes(p))
    }
    return user.permissions?.includes(permission)
  }, [user])

  // Check station access
  const hasStationAccess = useCallback((station) => {
    if (!user) return false
    if (user.permissions?.includes('all')) return true
    return user.stations?.includes(station)
  }, [user])

  // Refresh token function
  const refreshToken = useCallback(async () => {
    const refresh = localStorage.getItem('refreshToken')
    if (!refresh) return false

    try {
      const response = await api.post('/auth/refresh-token', { refreshToken: refresh })
      const { accessToken, refreshToken: newRefresh } = response.data.data

      localStorage.setItem('token', accessToken)
      if (newRefresh) localStorage.setItem('refreshToken', newRefresh)
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

      return true
    } catch (err) {
      logout()
      return false
    }
  }, [logout])

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    changePassword,
    hasPermission,
    hasStationAccess,
    refreshToken,
    setError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
